import { RefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { VsTabs } from '@quizzach/styles';

export type GiftTabKey = 'izd' | 'opce' | 'teme' | 'svi';

const GIFT_TABS: GiftTabKey[] = ['izd', 'opce', 'teme', 'svi'];
const TABS_STICK_TOP = 74;

/**
 * The gift shop's menu, on the shop and on the page for entering a code. The
 * nav bar leaves on scroll down and returns on scroll up: the sticky bar
 * follows it, so it never floats with a gap or sits under the nav.
 */
export const GiftTabs = ({
	active,
	flush,
	barRef,
	onSelect,
	onRedeem,
}: {
	active: GiftTabKey | 'redeem' | null;
	/** Nothing above the bar, so it needs no margin of its own. */
	flush?: boolean;
	barRef?: RefObject<HTMLDivElement>;
	onSelect: (key: GiftTabKey) => void;
	onRedeem: () => void;
}) => {
	const { t } = useTranslation();
	const ownRef = useRef<HTMLDivElement>(null);
	const ref = barRef ?? ownRef;
	const listRef = useRef<HTMLDivElement>(null);
	const [navShown, setNavShown] = useState(true);
	// once stuck, the bar also covers the strip above it (see VsTabs)
	const [stuck, setStuck] = useState(false);

	useEffect(() => {
		let last = window.scrollY;
		const onScroll = () => {
			const y = window.scrollY;
			const top = ref.current?.getBoundingClientRect().top ?? Infinity;
			setStuck(top <= TABS_STICK_TOP + 1);
			if (Math.abs(y - last) < 4) return;
			setNavShown(y < last || y < 80);
			last = y;
		};
		window.addEventListener('scroll', onScroll, { passive: true });
		return () => window.removeEventListener('scroll', onScroll);
	}, [ref]);

	// on phones the menu scrolls sideways: keep the highlighted item in view
	useEffect(() => {
		const list = listRef.current;
		const current = list?.querySelector<HTMLElement>('button.on');
		if (!list || !current || list.scrollWidth <= list.clientWidth) return;
		list.scrollTo({
			left: current.offsetLeft - (list.clientWidth - current.offsetWidth) / 2,
			behavior: 'smooth',
		});
	}, [active]);

	return (
		<VsTabs
			ref={ref}
			className={[flush && 'no-hero', navShown && 'nav', stuck && 'stuck']
				.filter(Boolean)
				.join(' ')}
		>
			<div className='list' ref={listRef}>
				{GIFT_TABS.map((key) => (
					<button
						key={key}
						type='button'
						className={active === key ? 'on' : ''}
						aria-current={active === key ? 'true' : undefined}
						onClick={() => onSelect(key)}
					>
						{t(`voucherShop.tabs.${key}`)}
					</button>
				))}
				<button
					type='button'
					className={active === 'redeem' ? 'on' : ''}
					aria-current={active === 'redeem' ? 'page' : undefined}
					onClick={onRedeem}
				>
					{t('voucherShop.redeem')}
				</button>
			</div>
		</VsTabs>
	);
};
